/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import React, { FC, Fragment } from "react"
import { InputNumber, Switch } from "@duxton-web/ui"
import { Form } from "grab-ui"
import { ModifierGroup } from "types/models/menu"
import { makeFmqMaxQuantityValidator } from "utils/form_helper"
import { useTranslationsMapTyped } from "hooks/useTranslationsMapTyped"

import styles from "./FlexibleQuantitySection.modules.css"

export const FMQ_ENABLED_FIELD = "fmqEnabled"
export const FMQ_MAX_QUANTITY_FIELD = "fmqMaxQuantity"

export interface FMQParams {
  isFlexibleQuantityEnabled?: boolean
  maxModifierSelectionQuantity?: number
}

interface FlexibleQuantitySectionProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: any
  /**
   * Per-merchant enableFlexibleModifierQuantityMexPhase2 flag
   * (menu.outletV2.featureFlags — see useIsFmqEnabled). Section is fully
   * hidden when false.
   */
  isFmqEnabled: boolean
  modifierGroup: ModifierGroup
  /** Live max selection of the group (reflects unsaved selection-rule edits). */
  selectionRangeMax: number
  isAllowEdit?: boolean
  onChange?: () => void
}

/**
 * FMQ (Flexible Modifier Quantity) settings for a modifier group — BP-48453.
 * Mirrors the MEX-app settings UI (BP-48187): an "Enable option quantity"
 * toggle plus a per-group "Maximum quantity" cap. Only meaningful for
 * multi-select groups, so it renders only when the group allows selecting
 * more than one option (Figma node 1542-23679 show/hide rules).
 */
export const FlexibleQuantitySection: FC<FlexibleQuantitySectionProps> = ({
  form,
  isFmqEnabled,
  modifierGroup,
  selectionRangeMax,
  isAllowEdit = true,
  onChange,
}) => {
  const textMap = useTranslationsMapTyped({
    enableLabel: "menu.modifierGroup.fmq.enableLabel",
    enableDescription: "menu.modifierGroup.fmq.enableDescription",
    maxQuantityLabel: "menu.modifierGroup.fmq.maxQuantityLabel",
    maxQuantitySmallerEqualError:
      "menu.modifierGroup.fmq.maxQuantitySmallerEqualError",
    maxQuantityMoreThanOneError:
      "menu.modifierGroup.fmq.maxQuantityMoreThanOneError",
    requiredError: "menu.errors.requiredField",
  })

  if (!isFmqEnabled || !(selectionRangeMax > 1)) {
    return null
  }

  const initialEnabled = !!modifierGroup.isFlexibleQuantityEnabled
  // An FMQ-enabled group with no stored cap is effectively capped at the
  // group's max selection — mirror that fallback in the input (MEX-app parity).
  const initialMaxQuantity =
    modifierGroup.maxModifierSelectionQuantity || selectionRangeMax

  const enabledValue = form.getFieldValue(FMQ_ENABLED_FIELD)
  const isEnabled = enabledValue !== undefined ? enabledValue : initialEnabled

  return (
    <Fragment>
      <div className={styles.fmqSection} data-testid="fmq-section">
        <div className={styles.toggleRow}>
          <div className={styles.toggleTextWrapper}>
            <span className={styles.toggleTitle}>{textMap.enableLabel}</span>
            <span className={styles.toggleDescription}>
              {textMap.enableDescription}
            </span>
          </div>
          <Form.Item className={styles.toggleFormItem}>
            {form.getFieldDecorator(FMQ_ENABLED_FIELD, {
              initialValue: initialEnabled,
              valuePropName: "checked",
            })(
              <Switch
                data-testid="fmq-enable-switch"
                disabled={!isAllowEdit}
                onChange={(checked: boolean) => {
                  // Reset the cap on every toggle so a stale unsaved value is
                  // never retained: clear it when switching off, and restore
                  // the live default (stored cap, else the group max) when
                  // switching on.
                  form.setFieldsValue({
                    [FMQ_MAX_QUANTITY_FIELD]: checked
                      ? initialMaxQuantity
                      : undefined,
                  })
                  onChange?.()
                }}
              />,
            )}
          </Form.Item>
        </div>
        {isEnabled && (
          <Form.Item
            hasFeedback
            label={<span>{textMap.maxQuantityLabel}</span>}
            className={styles.maxQuantityItem}
          >
            {form.getFieldDecorator(FMQ_MAX_QUANTITY_FIELD, {
              initialValue: initialMaxQuantity,
              rules: [
                {
                  required: true,
                  message: textMap.requiredError,
                },
                {
                  // Two-state cap validator: must be > 1 (multiple quantity)
                  // and ≤ the group's max number of selections.
                  validator: makeFmqMaxQuantityValidator(
                    selectionRangeMax,
                    textMap.maxQuantityMoreThanOneError,
                    textMap.maxQuantitySmallerEqualError,
                  ),
                },
              ],
            })(
              <InputNumber
                data-testid="fmq-max-quantity-input"
                className={styles.maxQuantityInput}
                controls={false}
                precision={0}
                disabled={!isAllowEdit}
                onChange={() => onChange?.()}
              />,
            )}
          </Form.Item>
        )}
      </div>
    </Fragment>
  )
}
